import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { AlertCircle, Calendar, FileText, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const ActiveAccountingWarnings = ({ ruleType }) => {
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadWarnings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/accounting/warnings/${ruleType}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`
        }
      });
      setWarnings(res.data || []);
    } catch (e) {
      console.error('Uyarılar yüklenemedi:', e);
    } finally {
      setLoading(false);
    }
  }, [ruleType]);

  useEffect(() => {
    loadWarnings();
  }, [loadWarnings]);

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Uyarılar yükleniyor...</div>;
  }

  if (warnings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 py-16 text-center">
        <div className="rounded-full bg-green-100 p-3 mb-3">
          <AlertCircle className="h-6 w-6 text-green-600" />
        </div>
        <p className="text-base font-medium text-gray-700">Harika!</p>
        <p className="mt-1 text-sm text-gray-500">Bu kategori için herhangi bir uyarı bulunmuyor.</p>
      </div>
    );
  }

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'fatura_kesilecek': return 'Fatura Kesilecek';
      case 'fatura_alinacak': return 'Fatura Alınacak';
      case 'eksik_fatura': return 'Eksik Fatura';
      default: return status;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-800">
          Aktif Uyarılar
          <Badge variant="destructive" className="ml-2 bg-red-100 text-red-700 hover:bg-red-200 border-0">
            {warnings.length} Adet
          </Badge>
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {warnings.map((w, idx) => (
          <Card key={`${w.id}-${idx}`} className={`border-2 shadow-sm hover:shadow-md transition-shadow ${w.kategori === 'kdv_odemesi' ? 'border-indigo-200' : 'border-red-200'}`}>
            <CardContent className="p-4">
              {w.kategori === 'kdv_odemesi' ? (
                <>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-start gap-3">
                      <div className="bg-indigo-100 p-2 rounded-lg mt-0.5">
                        <AlertCircle className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{w.rule_name}</h4>
                        <p className="text-sm text-gray-500 mt-0.5">Her ayın {w.odeme_gunu}. günü ödeme</p>
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap ml-2">
                      <span className={`font-semibold block text-lg ${w.summary.is_odenecek ? 'text-red-600' : 'text-emerald-600'}`}>
                        {formatMoney(Math.abs(w.summary.net_kdv))}
                      </span>
                      <span className={`text-xs font-medium ${w.summary.is_odenecek ? 'text-red-600' : 'text-emerald-600'}`}>
                        {w.summary.is_odenecek ? 'AÇIK (ÖDENECEK KDV)' : 'ARTI (DEVREDEN KDV)'}
                      </span>
                    </div>
                  </div>

                  {w.summary.rates && w.summary.rates.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <h5 className="text-sm font-medium text-gray-700 border-b pb-1">Oran Bazlı Detaylar</h5>
                      {w.summary.rates.map(r => (
                        <div key={r.rate} className="bg-gray-50 p-2 rounded text-sm grid grid-cols-3 gap-2 text-center">
                          <div className="text-left font-medium text-gray-700">%{r.rate} KDV</div>
                          <div>
                            <span className="block text-xs text-gray-500">Alınan (İndirilecek)</span>
                            <span className="text-emerald-600 font-medium">{formatMoney(r.alinan_kdv)}</span>
                          </div>
                          <div>
                            <span className="block text-xs text-gray-500">Kesilen (Hesaplanan)</span>
                            <span className="text-red-600 font-medium">{formatMoney(r.kesilen_kdv)}</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center text-sm font-medium bg-gray-100 p-2 rounded border border-gray-200 mt-2">
                        <span className="text-gray-700">Toplam:</span>
                        <div className="flex gap-4">
                          <span className="text-emerald-600">Alınan: {formatMoney(w.summary.total_alinan_kdv)}</span>
                          <span className="text-red-600">Kesilen: {formatMoney(w.summary.total_kesilen_kdv)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-start gap-3">
                      <div className="bg-red-100 p-2 rounded-lg mt-0.5">
                        <AlertCircle className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 line-clamp-2">{w.title}</h4>
                        <p className="text-sm text-gray-500 mt-0.5">Kural: {w.rule_name}</p>
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap ml-2">
                      <span className="font-semibold text-gray-900 block">{formatMoney(w.miktar)}</span>
                      <span className="text-xs font-medium text-red-600">
                        {w.days_passed} Gün Geçti
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 text-xs mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center text-gray-600 bg-gray-50 px-2 py-1 rounded">
                      <Calendar className="h-3.5 w-3.5 mr-1 text-gray-400" />
                      İşlem: {w.tarih ? format(new Date(w.tarih), 'dd MMM yyyy', { locale: tr }) : '-'}
                    </div>
                    <div className="flex items-center text-gray-600 bg-gray-50 px-2 py-1 rounded">
                      <FileText className="h-3.5 w-3.5 mr-1 text-gray-400" />
                      {getStatusLabel(w.fatura_durumu)}
                    </div>
                    {w.days_exceeded > 0 && (
                      <div className="flex items-center text-red-700 bg-red-50 px-2 py-1 rounded font-medium border border-red-100">
                        <ArrowRight className="h-3.5 w-3.5 mr-1 text-red-500" />
                        Kuralı {w.days_exceeded} gün aştı
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ActiveAccountingWarnings;
